import React, { ChangeEvent, useEffect, useState, useMemo } from 'react';
import { Modal, Button, Input, Form, Typography } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
// Api
import { GET_CB_APPLICATION_ANSWERS } from 'api/campaignV2/queries';
import { UPDATE_CB_APPLICATION_ANSWER } from 'api/campaignV2/mutations';
// Types
import { CbApplicationQuestionType } from 'api/graphql-global-types';
import { GetCampaignOffers_getCampaignOffers_entities } from 'api/campaignV2/types/GetCampaignOffers';
import {
  GetCbApplicationAnswers,
  GetCbApplicationAnswers_getCbApplicationAnswers,
  GetCbApplicationAnswersVariables,
} from 'api/campaignV2/types/GetCbApplicationAnswers';
import {
  UpdateCbApplicationAnswer,
  UpdateCbApplicationAnswerVariables,
} from 'api/campaignV2/types/UpdateCbApplicationAnswer';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Style
import styles from './ManageTalentAnswersModal.module.scss';

const { TextArea } = Input;
const { Text } = Typography;

type CampaignDeliverablesReviewProps = {
  offer: GetCampaignOffers_getCampaignOffers_entities;
  onClose: () => void;
};

type AnswerState = Record<string, string>;

const ManageTalentAnswersModal = ({
  offer,
  onClose,
}: CampaignDeliverablesReviewProps): JSX.Element => {
  const { data, loading, refetch } = useQuery<
    GetCbApplicationAnswers,
    GetCbApplicationAnswersVariables
  >(GET_CB_APPLICATION_ANSWERS, {
    variables: { input: { offerId: offer.id } },
  });

  const [updateCbApplicationAnswer] = useMutation<
    UpdateCbApplicationAnswer,
    UpdateCbApplicationAnswerVariables
  >(UPDATE_CB_APPLICATION_ANSWER);

  const fetchedAnswers = data?.getCbApplicationAnswers;

  const questions = useMemo(() => fetchedAnswers || [], [fetchedAnswers]);

  const [answers, setAnswers] = useState<AnswerState>({});

  useEffect(() => {
    if (questions.length) {
      const initial: AnswerState = {};
      questions.forEach((ans) => {
        initial[ans.id] = ans.answer;
      });
      setAnswers(initial);
    }
  }, [questions]);

  const handleChange = (id: string, e: ChangeEvent<HTMLTextAreaElement>) => {
    setAnswers((prev) => ({ ...prev, [id]: e.target.value }));
  };

  const pitchQuestions = questions.filter(
    (ans) => ans.question.type === CbApplicationQuestionType.Pitch
  );
  const regularQuestions = questions.filter(
    (ans) => ans.question.type === CbApplicationQuestionType.Regular
  );

  const handleSave = async (answerId: string, answer: string) => {
    try {
      await updateCbApplicationAnswer({
        variables: {
          input: {
            answerId,
            answer,
          },
        },
      });

      successNotification('Answer updated!');
      refetch();
    } catch (error) {
      errorNotification((error as Error)?.message || 'Something went wrong');
    }
  };

  const renderAnswerItem = (
    item: GetCbApplicationAnswers_getCbApplicationAnswers
  ) => (
    <Form.Item
      key={item.id}
      label={item.question.question}
      required={item.question.isRequired}
      className={styles.form}
    >
      <div className={styles.answer}>
        <Text strong>Original Answer:</Text> <Text>{item.originalAnswer}</Text>
      </div>

      <TextArea
        rows={4}
        value={answers[item.id]}
        onChange={(e) => handleChange(item.id, e)}
        placeholder={`Edit your answer for "${item.question.question}"`}
      />

      <div className={styles.buttonWrapper}>
        <Button
          type="primary"
          loading={loading}
          onClick={() => handleSave(item.id, answers[item.id])}
        >
          Save
        </Button>
      </div>
    </Form.Item>
  );

  const hasPitchQuestions = pitchQuestions.length > 0;
  const hasRegularQuestions = regularQuestions.length > 0;

  return (
    <Modal
      title="Manage answers"
      visible={!!offer}
      width={850}
      onCancel={onClose}
      footer={null}
    >
      <Form layout="vertical">
        {hasPitchQuestions ? (
          pitchQuestions.map(renderAnswerItem)
        ) : (
          <p>No Pitch answers</p>
        )}

        {hasRegularQuestions ? (
          regularQuestions.map(renderAnswerItem)
        ) : (
          <p>No Regular Answers</p>
        )}
      </Form>
    </Modal>
  );
};

export default ManageTalentAnswersModal;
